<template>
  <v-dialog v-model="dialogModel" max-width="500" persistent>
    <v-card>
      <v-card-title class="d-flex align-center">
        <v-icon class="mr-2" color="warning">mdi-arrow-up-bold</v-icon>
        Escalar incidencia
      </v-card-title>
      <v-card-subtitle v-if="incident">
        #{{ incident.id }} — {{ incident.title }}
      </v-card-subtitle>

      <v-card-text>
        <v-alert type="info" variant="tonal" class="mb-4" density="compact">
          El escalamiento notifica a un rol superior sin cambiar el estado ni la asignación.
        </v-alert>

        <!-- P13-V1D: sin proyección del servidor no hay destinos — estado honesto, sin fallback local -->
        <v-alert
          v-if="availableTargetRoles.length === 0"
          type="warning"
          variant="tonal"
          class="mb-4"
          density="compact"
        >
          El servidor no proyectó destinos de escalación para esta incidencia.
        </v-alert>

        <!-- Selector de rol destino (SOLO destinos proyectados por el servidor) -->
        <v-select
          v-model="selectedRole"
          :items="availableTargetRoles"
          item-title="label"
          item-value="value"
          label="Rol destino"
          variant="outlined"
          :rules="[v => !!v || 'Selecciona un rol destino']"
          class="mb-2"
        />

        <!-- Motivo -->
        <v-textarea
          v-model="reason"
          label="Motivo de escalación"
          hint="Describe por qué se necesita intervención del rol superior"
          :rules="[v => (!!v && v.trim().length > 0) || 'El motivo es obligatorio']"
          rows="3"
          counter
          variant="outlined"
        />

        <v-alert v-if="errorMsg" type="error" variant="tonal" class="mt-2" density="compact" closable @click:close="errorMsg = ''">
          {{ errorMsg }}
        </v-alert>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn variant="text" @click="close">Cancelar</v-btn>
        <v-btn
          color="warning"
          variant="flat"
          :loading="submitting"
          :disabled="!canSubmit"
          @click="submit"
        >
          <v-icon start>mdi-arrow-up-bold</v-icon>
          Escalar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { incidentInboxService, type InboxAction } from '@/services/incidentInboxService'

// ═══════════════════════════════════════════════════════════════════════════════════════════
// P13-V1D (decisión congelada #8 — FRONTEND_ESCALATION_POLICY = ZERO):
// la tabla local de pares (espejo del spec §4.1) fue ELIMINADA y NO existe módulo espejo. El
// backend es la ÚNICA autoridad de pares: este diálogo renderiza exclusivamente los targets que
// el servidor proyecta en `availableActions.escalate.constraints.allowedTargetRoles` y jamás
// computa nada desde el rol del usuario (el POST además revalida TODO en la fence).
// ROLE_DISPLAY_LABELS es presentación pura (etiqueta ES de un valor que ya llegó del servidor);
// un rol no listado se muestra con su valor crudo — nunca se filtra ni se añade nada.
// ═══════════════════════════════════════════════════════════════════════════════════════════

const ROLE_DISPLAY_LABELS: Record<string, string> = {
  field_supervisor: 'Supervisor de campo',
  department_head: 'Jefe de departamento',
}

// ═══════════════════════════════════════════════════════════
// Props & emits
// ═══════════════════════════════════════════════════════════

const props = defineProps<{
  modelValue: boolean
  incident: {
    id: number
    title: string
    status: string
    availableActions?: InboxAction[]
  } | null
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', val: boolean): void
  (e: 'escalated', data: any): void
}>()

// ═══════════════════════════════════════════════════════════
// State
// ═══════════════════════════════════════════════════════════

const dialogModel = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val),
})

const selectedRole = ref('')
const reason = ref('')
const submitting = ref(false)
const errorMsg = ref('')

// ÚNICA fuente: la proyección del servidor sobre la incidencia concreta. Sin proyección → sin
// opciones (el botón de escalar tampoco existiría: proviene de availableActions del servidor).
const availableTargetRoles = computed(() => {
  const escalateAction = props.incident?.availableActions?.find((a) => a.action === 'escalate')
  const projected = escalateAction?.constraints?.allowedTargetRoles
  if (!Array.isArray(projected)) return []
  return projected.map((role) => ({
    value: role,
    label: ROLE_DISPLAY_LABELS[role] || role,
  }))
})

const canSubmit = computed(() => {
  return !!selectedRole.value && !!reason.value?.trim()
})

// Reset on open
watch(() => props.modelValue, (val) => {
  if (val) {
    selectedRole.value = ''
    reason.value = ''
    errorMsg.value = ''
  }
})

// ═══════════════════════════════════════════════════════════
// Actions
// ═══════════════════════════════════════════════════════════

function close() {
  dialogModel.value = false
}

async function submit() {
  if (!props.incident || !canSubmit.value) return

  submitting.value = true
  errorMsg.value = ''

  try {
    const result = await incidentInboxService.escalateIncident(
      props.incident.id,
      selectedRole.value,
      reason.value.trim()
    )
    emit('escalated', result.data)
    close()
  } catch (err: any) {
    const msg = err?.response?.data?.error || err.message || 'Error al escalar'
    errorMsg.value = msg
  } finally {
    submitting.value = false
  }
}
</script>
